// Barber Data Service - Central source of truth for staff/barber information
// This connects Operations module with Staff Management module

import { isDayEnabled } from '../shared/availability';
import {
  collection,
  getDocs,
  onSnapshot,
  doc,
  updateDoc,
  setDoc,
  addDoc,
  deleteDoc,
  Timestamp
} from 'firebase/firestore';
import { db, storage, auth } from '../config/firebase';
import { onAuthStateChanged } from 'firebase/auth';
import { ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import { getOperatorDb, getOperatorBarberId, onOperatorChange } from './operatorSession';
import toast from 'react-hot-toast';

// Per-day schedule slot. DUAL-SCHEMA: newer records write `enabled`, older
// records write `available`. Both optional; readers use `enabled ?? available ?? true`.
export interface DayAvailability {
  start: string;
  end: string;
  enabled?: boolean;
  available?: boolean;
  breakStart?: string;
  breakEnd?: string;
}

// Growth System (2026-08-22): auto-computed business/development snapshot persisted per barber.
// Written by growthMetricsService.recomputeAndPersist*; read by the profile / coaching / goals surfaces.
// BEHAVIOR-PIN: SLIK-GROWTH-SYSTEM-BLUEPRINT.md · shared/growth.ts is the math; joinDate is the tenure anchor.
export interface BarberGrowthSnapshot {
  score: number;                         // 0-100 composite over active-stage metrics
  stage: 1 | 2 | 3 | 4;
  stageName: string;
  tier: 'none' | 'bronze' | 'silver' | 'gold';
  provisional: boolean;                  // advanced via grind backstop without mastery → bonus capped
  bonusEligible: boolean;
  bonusPct: number;                      // % of services (tier bonus)
  bonusEstWeekly: number;                // $ estimate for the measured week
  occupancyTarget: number;              // stage occupancy "full marks" bar
  components: { occupancy: number; rebooking: number; returning: number; retail: number; rating: number };
  weakest: string | null;                // lowest active component key → coaching focus
  shouldPromote: boolean;                // advancement recommendation
  advancementReason: string;
  priceIncreaseEligible: boolean;
  // Display fields for the coaching + finance surfaces (computed at recompute time):
  nudges?: Array<{ key: string; label: string; message: string; dollarPerWeek: number | null }>;
  weeklyServiceRevenue?: number;         // for the earnings breakdown
  weeklyRetailDollars?: number;          // for the retail spiff line
  occupancyPct?: number;                 // CURRENT occupancy % (booked ÷ available hours) — capacity/projection
  availableHoursPerWeek?: number;        // schedulable hours/week (minus blocks) — projection input
  payWeekRevenue?: number;               // service $ booked-to-date THIS pay week — live commission-cliff card
  payWeekStart?: string;                 // ISO date (YYYY-MM-DD) of this pay week's start
  commissionTiers?: { min: number; rate: number }[]; // the shop's configured weekly tiers (for the card)
  retailSpiffPct?: number;               // configured retail spiff %
  updatedAt: string;                     // ISO timestamp
}

export interface Barber {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  photo?: string;
  role: string;
  skills: string[];
  certifications: string[];
  experience: number;
  languages: string[];

  // Per-barber service config (Jose 2026-10-17): override the shop-default duration/price per service
  // ("Diana needs 45 for a fade, mine's 30"; "my prices may be higher"). Keyed by normalized service
  // name; absent entry = use the shop default. Resolved via shared/serviceDuration (resolveService*).
  serviceDurations?: Record<string, number>;
  servicePrices?: Record<string, number>;
  // Per-barber TRUST grants above the role default (overrideConflict/lateCheckout/editPastAppointments/
  // applyDiscount/voidTransaction). Read by the authority brain (shared/authority.ts). Absent = role default.
  capabilities?: Record<string, boolean>;

  // Performance metrics
  avgServiceTime: number;
  avgRating: number;
  monthlyRevenue: number;
  returningClients: number;
  totalClients: number;

  // Schedule
  // DUAL-SCHEMA: newer records use `enabled`, older ones use `available`.
  // Readers resolve `enabled ?? available ?? true` (see shared/availability.ts).
  availability: {
    monday: DayAvailability;
    tuesday: DayAvailability;
    wednesday: DayAvailability;
    thursday: DayAvailability;
    friday: DayAvailability;
    saturday: DayAvailability;
    sunday: DayAvailability;
  };

  // Career progression
  careerStage: 'Apprentice' | 'Junior' | 'Barber' | 'Senior' | 'Master' | 'Elite' | 'Partner';
  equity?: number;
  commissionRate: number;
  // Per-barber pay mode (Jose 2026-09-01). 'sliding' = the shop's Commission & Pay cliff scale
  // (the default — one place to look, Settings → Commission & Pay); 'flat' = override this barber
  // to a fixed `commissionRate` %. Undefined/legacy resolves to 'sliding'. Payroll reads this
  // (reportingDataService.getPayrollReport); the toggle lives in BarberOnboardingModal.
  commissionMode?: 'sliding' | 'flat';

  // Growth System snapshot (auto-computed; see BarberGrowthSnapshot). Optional → safe for legacy docs.
  growth?: BarberGrowthSnapshot;

  // Sliding Scale Commission Structure
  commissionTiers?: {
    enabled: boolean;
    tiers: Array<{
      minRevenue: number;
      maxRevenue: number;
      rate: number;
    }>;
  };
  currentTier?: {
    rate: number;
    minRevenue: number;
    maxRevenue: number;
  };

  // Additional fields
  joinDate: Date;
  status: 'active' | 'inactive' | 'on-leave' | 'deleted';
  notes?: string;
  colorCode?: string; // For calendar display

  // Emergency Booking Settings
  emergencyBooking?: {
    enabled: boolean;           // Whether barber can take emergency bookings
    enabledAt?: Date;           // When emergency booking was enabled
    enabledBy?: string;         // Admin who enabled it
    unlockReason?: 'bpi_threshold' | 'manual_grant'; // How it was unlocked
  };

  // PIN login (BEHAVIOR-PIN 2026-09-29): the raw PIN no longer lives on the barbers doc — it's a
  // HASHED secret in Admin-only barberSecrets, verified server-side via barberSignIn. Keep only a flag.
  hasPinLogin?: boolean;           // true once a PIN login is provisioned (provisionBarberLogin)
  requirePinOnWake?: boolean;      // Require PIN when screen wakes
  requirePinOnOpen?: boolean;      // Require PIN when app opens
  autoLogoutMinutes?: number;      // Auto-lock after X minutes of inactivity

  // 📍 MULTI-LOCATION SUPPORT
  locationIds?: string[]; // Locations where this barber works — CANONICAL (wizard "Works At" writes here; locationService reads it)
  availabilityGroups?: string[]; // Shared-availability keys for co-located front⇄back blocking

  // 🪪 LICENSE (2026-08-02) — captured in the Add-Barber wizard
  licensePhoto?: string;
  licenseNumber?: string;
  licenseExpiry?: string;
}

// Sample barber data - EMPTY by default, will load from Firebase only
const barbersDatabase: Barber[] = [];

class BarberDataService {
  private barbers: Barber[] = [];
  private barbersCollection = collection(db, 'barbers');
  private listeners: ((barbers: Barber[]) => void)[] = [];
  private isInitialized = false;

  // SECURITY (Phase B 2026-09-30): pay (monthlyRevenue/commissionRate/commissionMode) lives in the
  // locked `barberPrivate/{id}` and is read ONLY through the ephemeral OPERATOR session (the person
  // tapped in) — the shared iPad account holds NO pay-reading power. Owner/manager operator → the whole
  // collection; a barber operator → ONLY their own doc (an unconstrained collection query would be
  // rule-denied). Nobody tapped in → no pay merged. Re-subscribes on every operator change.
  private privatePay: Record<string, { monthlyRevenue?: number; commissionRate?: number; commissionMode?: 'sliding' | 'flat' }> = {};
  private privateUnsub: (() => void) | null = null;
  private _barbersUnsub: (() => void) | null = null; // barbers listener handle (for auth re-subscribe)

  constructor() {
    this.initializeRealTimeSync();
    this.initializePrivatePaySync();
    // Load from localStorage as fallback
    this.loadFromLocalStorage();
    // RESILIENCE (Jose 2026-10-29 audit): the barbers listener above can attach pre-auth (module load before
    // sign-in). It already has an error cb + localStorage fallback, but without a re-subscribe it would stay
    // on the cached list for the whole session. Re-subscribe when auth resolves so it goes live. (pay sync
    // already re-subscribes via onOperatorChange.)
    try { onAuthStateChanged(auth, (u) => { if (u) this.initializeRealTimeSync(); }); } catch { /* ignore */ }
  }

  // Merge the operator-scoped pay fields (from barberPrivate) onto a barber. No-op if not loaded.
  private mergePrivate(b: Barber): Barber {
    const p = this.privatePay[b.id];
    if (!p) return b;
    return {
      ...b,
      monthlyRevenue: p.monthlyRevenue ?? b.monthlyRevenue,
      commissionRate: p.commissionRate ?? b.commissionRate,
      commissionMode: p.commissionMode ?? b.commissionMode,
    };
  }

  private applyPrivate(map: Record<string, { monthlyRevenue?: number; commissionRate?: number; commissionMode?: 'sliding' | 'flat' }>) {
    this.privatePay = map;
    if (this.barbers.length) {
      this.barbers = this.barbers.map((b) => this.mergePrivate(b));
      this.saveToLocalStorage();
      this.notifyListeners();
    }
  }

  // Subscribe to barberPrivate through the OPERATOR session if tapped in, ELSE the main login (an
  // owner/manager logged in directly reads all; the bare shop account is rule-denied → empty). Try the
  // whole collection; on denial the reader is a barber → fall back to their own doc. Re-subscribe on
  // every operator change.
  private initializePrivatePaySync() {
    type PayMap = Record<string, { monthlyRevenue?: number; commissionRate?: number; commissionMode?: 'sliding' | 'flat' }>;
    const resubscribe = () => {
      if (this.privateUnsub) { this.privateUnsub(); this.privateUnsub = null; }
      const rdb = getOperatorDb() || db;
      const bid = getOperatorBarberId();
      const subOwn = () => {
        if (!bid) { this.applyPrivate({}); return; }
        this.privateUnsub = onSnapshot(doc(rdb, 'barberPrivate', bid), (d) => {
          const map: PayMap = {};
          if (d.exists()) { const p = d.data() as any; map[bid] = { monthlyRevenue: p.monthlyRevenue, commissionRate: p.commissionRate, commissionMode: p.commissionMode }; }
          this.applyPrivate(map);
        }, () => this.applyPrivate({}));
      };
      let fellBack = false;
      this.privateUnsub = onSnapshot(collection(rdb, 'barberPrivate'), (snap) => {
        const map: PayMap = {};
        snap.forEach((d) => { const p = d.data() as any; map[d.id] = { monthlyRevenue: p.monthlyRevenue, commissionRate: p.commissionRate, commissionMode: p.commissionMode }; });
        this.applyPrivate(map);
      }, () => {
        // Collection denied → a barber identity → read only their own doc.
        if (!fellBack) { fellBack = true; if (this.privateUnsub) this.privateUnsub(); subOwn(); }
      });
    };
    resubscribe();
    onOperatorChange(resubscribe);
  }

  // MONEY-CRITICAL (Jose 2026-10-28): payroll/performance must NEVER compute commission on an un-merged
  // barber — the private pay vault (commissionMode/rate) loads ASYNC via onSnapshot, so a cold payroll run
  // could read a FLAT-50 barber as having no mode → commissionForBarber falls back to the SLIDING scale
  // and underpays. This does a synchronous (awaited) authoritative read of barberPrivate and re-merges, so
  // a flat barber is always flat. Owner reads the whole collection; a barber identity is denied → the live
  // onSnapshot own-doc path already covers them, so a denial here is a safe no-op.
  async ensurePrivatePayLoaded(): Promise<void> {
    try {
      const rdb = getOperatorDb() || db;
      const snap = await getDocs(collection(rdb, 'barberPrivate'));
      const map: Record<string, { monthlyRevenue?: number; commissionRate?: number; commissionMode?: 'sliding' | 'flat' }> = {};
      snap.forEach((d) => {
        const p = d.data() as any;
        map[d.id] = { monthlyRevenue: p.monthlyRevenue, commissionRate: p.commissionRate, commissionMode: p.commissionMode };
      });
      if (Object.keys(map).length) this.applyPrivate(map);
    } catch { /* collection denied (barber identity) → the onSnapshot own-doc path covers it */ }
  }

  // Initialize real-time sync with Firebase
  private initializeRealTimeSync() {
    try { this._barbersUnsub?.(); } catch { /* ignore */ }
    this._barbersUnsub = onSnapshot(this.barbersCollection, (snapshot) => {
      const firebaseBarbers: Barber[] = [];
      snapshot.forEach((doc) => {
        const data = doc.data();
        firebaseBarbers.push({
          // PERSISTENCE FIX (2026-08-06): pass through EVERY stored field FIRST, then let the
          // typed conversions/defaults below OVERRIDE where needed. The old mapper cherry-picked
          // ~35 fields and silently DROPPED permissions, assignedServices, accessLevel, ppiScores,
          // ppiHistory, workSchedule, bio, name, social, station/hire fields, etc. Every save DID
          // reach Firestore, but this snapshot listener stripped those fields on the way back into
          // memory — so edits "never persisted". Spreading data keeps them all and kills this whole
          // class of drop-on-read bug permanently (new modal fields survive automatically).
          ...(data as any),
          id: doc.id,
          firstName: data.firstName || '',
          lastName: data.lastName || '',
          email: data.email || '',
          phone: data.phone || '',
          photo: data.photo,
          role: data.role || 'Barber',
          skills: data.skills || [],
          certifications: data.certifications || [],
          experience: data.experience || 0,
          languages: data.languages || ['English'],
          serviceDurations: data.serviceDurations || {},
          servicePrices: data.servicePrices || {},
          avgServiceTime: data.avgServiceTime || 30,
          avgRating: data.avgRating || 0,
          monthlyRevenue: data.monthlyRevenue || 0,
          returningClients: data.returningClients || 0,
          totalClients: data.totalClients || 0,
          availability: data.availability || this.getDefaultAvailability(),
          careerStage: data.careerStage || 'Barber',
          equity: data.equity,
          commissionRate: data.commissionRate || 40,
          commissionMode: data.commissionMode,
          commissionTiers: data.commissionTiers,
          currentTier: data.currentTier,
          joinDate: data.joinDate?.toDate() || new Date(),
          status: data.status || 'active',
          notes: data.notes,
          colorCode: data.colorCode,
          // PIN login flag (raw pin no longer stored on the barbers doc — see barberSecrets)
          hasPinLogin: data.hasPinLogin,
          requirePinOnWake: data.requirePinOnWake,
          requirePinOnOpen: data.requirePinOnOpen,
          autoLogoutMinutes: data.autoLogoutMinutes,
          // Emergency Booking
          emergencyBooking: data.emergencyBooking,
          // Multi-location (read either key — older wizard writes wrote worksAtLocations)
          locationIds: data.locationIds || data.worksAtLocations,
          availabilityGroups: data.availabilityGroups,
          // License
          licensePhoto: data.licensePhoto,
          licenseNumber: data.licenseNumber,
          licenseExpiry: data.licenseExpiry
        });
      });

      // Only use Firebase data - no fallback sample data
      if (firebaseBarbers.length > 0) {
        this.barbers = firebaseBarbers.map((b) => this.mergePrivate(b)); // merge staff-only pay
        this.isInitialized = true;
        this.saveToLocalStorage();
        this.notifyListeners();
      } else if (!this.isInitialized) {
        // No Firebase data - clear any cached data
        this.barbers = [];
        this.isInitialized = true;
        localStorage.removeItem('slikpos_barbers');
        this.notifyListeners();
      }
    }, (error) => {
      console.error('Firebase sync error:', error);
      // Only use real data from localStorage if available
      this.loadFromLocalStorage();
      // Don't use sample data - production app should only have real barbers
    });
  }

  // Get default availability
  // DUAL-SCHEMA: write BOTH `enabled` and `available` (same value) so old and new
  // readers agree — see shared/availability.ts (enabled ?? available ?? true).
  private getDefaultAvailability() {
    return {
      monday: { start: '9:00', end: '18:00', enabled: true, available: true },
      tuesday: { start: '9:00', end: '18:00', enabled: true, available: true },
      wednesday: { start: '9:00', end: '18:00', enabled: true, available: true },
      thursday: { start: '9:00', end: '18:00', enabled: true, available: true },
      friday: { start: '9:00', end: '18:00', enabled: true, available: true },
      saturday: { start: '9:00', end: '17:00', enabled: true, available: true },
      sunday: { start: '', end: '', enabled: false, available: false }
    };
  }

  // Sync sample data to Firebase if collection is empty
  private async syncSampleDataToFirebase() {
    try {
      for (const barber of barbersDatabase) {
        const { id, ...barberData } = barber;
        await addDoc(this.barbersCollection, {
          ...barberData,
          joinDate: Timestamp.fromDate(barber.joinDate)
        });
      }
      console.log('Sample barber data synced to Firebase');
    } catch (error) {
      console.error('Error syncing sample barber data:', error);
    }
  }

  // Subscribe to changes
  subscribe(callback: (barbers: Barber[]) => void): () => void {
    this.listeners.push(callback);
    // Immediately call with current data
    callback(this.barbers);

    // Return unsubscribe function
    return () => {
      const index = this.listeners.indexOf(callback);
      if (index > -1) {
        this.listeners.splice(index, 1);
      }
    };
  }

  // Notify all listeners
  private notifyListeners() {
    this.listeners.forEach(callback => callback(this.barbers));
  }

  // Barbers visible to OPERATIONAL surfaces (calendar, booking, queue, checkout, login):
  // exclude archived ('inactive') and deleted. ONE choke point so archive/delete propagate
  // everywhere consistently (2026-08-02). getBarberById() stays unfiltered so historical
  // appointments can still resolve an archived barber's name.
  private operationalBarbers(): Barber[] {
    return this.barbers.filter(b => b.status !== 'inactive' && b.status !== 'deleted');
  }

  // Get all barbers - synchronous. Default HIDES archived/deleted; pass includeArchived=true
  // for admin surfaces that need everyone (staff Archived view, CSV-dedup, migration).
  getAllBarbers(includeArchived: boolean = false): Barber[] {
    return includeArchived ? this.barbers : this.operationalBarbers();
  }

  // Get all barbers - async version
  async getAllBarbersAsync(includeArchived: boolean = false): Promise<Barber[]> {
    if (this.barbers.length === 0) {
      try {
        const snapshot = await getDocs(this.barbersCollection);
        const barbers: Barber[] = [];
        snapshot.forEach((doc) => {
          const data = doc.data();
          barbers.push({
            id: doc.id,
            ...this.convertFirebaseBarber(data)
          });
        });
        if (barbers.length > 0) {
          this.barbers = barbers.map((b) => this.mergePrivate(b)); // merge staff-only pay
          this.saveToLocalStorage();
        }
      } catch (error) {
        console.error('Error fetching barbers:', error);
      }
    }
    return includeArchived ? this.barbers : this.operationalBarbers();
  }

  // Convert Firebase data to Barber type
  private convertFirebaseBarber(data: any): Omit<Barber, 'id'> {
    return {
      // PERSISTENCE FIX (2026-08-06): same drop-on-read fix as the onSnapshot mapper above —
      // spread the full stored doc first so permissions/assignedServices/accessLevel/ppiScores/
      // workSchedule/etc. survive the async read path too; typed defaults below override.
      ...(data as any),
      firstName: data.firstName || '',
      lastName: data.lastName || '',
      email: data.email || '',
      phone: data.phone || '',
      photo: data.photo,
      role: data.role || 'Barber',
      skills: data.skills || [],
      certifications: data.certifications || [],
      experience: data.experience || 0,
      languages: data.languages || ['English'],
      serviceDurations: data.serviceDurations || {},
      servicePrices: data.servicePrices || {},
      avgServiceTime: data.avgServiceTime || 30,
      avgRating: data.avgRating || 0,
      monthlyRevenue: data.monthlyRevenue || 0,
      returningClients: data.returningClients || 0,
      totalClients: data.totalClients || 0,
      availability: data.availability || this.getDefaultAvailability(),
      careerStage: data.careerStage || 'Barber',
      equity: data.equity,
      commissionRate: data.commissionRate || 40,
      commissionMode: data.commissionMode,
      commissionTiers: data.commissionTiers,
      currentTier: data.currentTier,
      joinDate: data.joinDate?.toDate() || new Date(),
      status: data.status || 'active',
      notes: data.notes,
      colorCode: data.colorCode,
      // PIN login flag (raw pin no longer stored on the barbers doc — see barberSecrets)
      hasPinLogin: data.hasPinLogin,
      requirePinOnWake: data.requirePinOnWake,
      requirePinOnOpen: data.requirePinOnOpen,
      autoLogoutMinutes: data.autoLogoutMinutes,
      // Emergency Booking
      emergencyBooking: data.emergencyBooking,
      // Multi-location (read either key — older wizard writes wrote worksAtLocations)
      locationIds: data.locationIds || data.worksAtLocations,
      availabilityGroups: data.availabilityGroups,
      // License
      licensePhoto: data.licensePhoto,
      licenseNumber: data.licenseNumber,
      licenseExpiry: data.licenseExpiry
    };
  }

  // Get barber by ID
  getBarberById(id: string): Barber | undefined {
    return this.barbers.find(barber => barber.id === id);
  }

  // Get active barbers (exclude deleted ones)
  getActiveBarbers(): Barber[] {
    return this.barbers.filter(barber => barber.status === 'active');
  }

  // Get barbers by career stage
  getBarbersByCareerStage(stage: Barber['careerStage']): Barber[] {
    return this.barbers.filter(barber => barber.careerStage === stage);
  }

  // Get barbers available on a specific day
  getBarbersAvailableOnDay(day: keyof Barber['availability']): Barber[] {
    return this.barbers.filter(barber => isDayEnabled(barber.availability[day]));
  }

  // Add new barber
  async addBarber(barber: Omit<Barber, 'id'>): Promise<Barber> {
    // BEHAVIOR-PIN (2026-09-29): keep sensitive fields off the world-readable barbers doc. Pay →
    // barberPrivate; a raw `pin` is dropped (PIN is set separately via provisionBarberPin/onboarding).
    const { pin: _pin, monthlyRevenue, commissionRate, commissionMode, ...safeBarber } = barber as any;
    try {
      const docRef = await addDoc(this.barbersCollection, {
        ...safeBarber,
        joinDate: Timestamp.fromDate(barber.joinDate)
      });
      const payUpdate: any = {};
      if (monthlyRevenue !== undefined) payUpdate.monthlyRevenue = monthlyRevenue;
      if (commissionRate !== undefined) payUpdate.commissionRate = commissionRate;
      if (commissionMode !== undefined) payUpdate.commissionMode = commissionMode;
      if (Object.keys(payUpdate).length) {
        try {
          await setDoc(doc(db, 'barberPrivate', docRef.id), { ...payUpdate, barberId: docRef.id, updatedAt: Timestamp.now() }, { merge: true });
          this.privatePay[docRef.id] = { ...payUpdate };
        } catch (e) { console.error('[barberDataService] addBarber pay write failed:', e); }
      }
      const newBarber: Barber = {
        ...barber,
        id: docRef.id
      };
      this.barbers.push(newBarber);
      this.saveToLocalStorage();
      this.notifyListeners();
      return newBarber;
    } catch (error) {
      console.error('Error adding barber:', error);
      // Fallback to local only
      const newBarber: Barber = {
        ...barber,
        id: `b${Date.now()}`
      };
      this.barbers.push(newBarber);
      this.saveToLocalStorage();
      return newBarber;
    }
  }

  // Update barber
  async updateBarber(id: string, updates: Partial<Barber>): Promise<Barber | undefined> {
    // BEHAVIOR-PIN (2026-09-29): sensitive fields never touch the world-readable barbers doc.
    // Pay (monthlyRevenue/commissionRate/commissionMode) is routed to the staff-locked barberPrivate;
    // a raw `pin` is dropped entirely (PIN changes go through provisionBarberPin / onboarding).
    const { pin: _pin, monthlyRevenue, commissionRate, commissionMode, ...safeUpdates } = updates as any;
    const payUpdate: any = {};
    if (monthlyRevenue !== undefined) payUpdate.monthlyRevenue = monthlyRevenue;
    if (commissionRate !== undefined) payUpdate.commissionRate = commissionRate;
    if (commissionMode !== undefined) payUpdate.commissionMode = commissionMode;
    if (Object.keys(payUpdate).length) {
      try {
        // Writes stay on the shop account (barberPrivate WRITE rule = isStaff). Security is on the READ
        // side (owner/operator only) — a write can't leak data, so no operator routing needed here.
        await setDoc(doc(db, 'barberPrivate', id), { ...payUpdate, barberId: id, updatedAt: Timestamp.now() }, { merge: true });
        this.privatePay[id] = { ...this.privatePay[id], ...payUpdate };
      } catch (e) {
        console.error('[barberDataService] failed to write pay to barberPrivate:', e);
      }
    }
    // Local cache reflects the FULL change (safe + pay) for immediate UI; only safe fields hit barbers.
    const localUpdates: Partial<Barber> = { ...safeUpdates, ...payUpdate };
    const safeOnly = safeUpdates as Partial<Barber>;
    try {
      if (Object.keys(safeOnly).length) {
        const docRef = doc(this.barbersCollection, id);
        try {
          await updateDoc(docRef, safeOnly as any);
        } catch (e: any) {
          // PERSISTENCE FIX (2026-08-05): updateDoc requires the doc to already exist. Seeded /
          // admin / local-only barber records (e.g. Jose's own profile) may not have a real
          // barbers/{id} document, so updateDoc throws 'not-found' and edits silently vanish on
          // reload. Upsert via setDoc(merge) so the save actually persists. Other errors rethrow.
          if (e?.code === 'not-found') {
            await setDoc(docRef, safeOnly as any, { merge: true });
          } else {
            throw e;
          }
        }
      }

      const index = this.barbers.findIndex(b => b.id === id);
      if (index !== -1) {
        this.barbers[index] = { ...this.barbers[index], ...localUpdates };
        this.saveToLocalStorage();
        this.notifyListeners();
        return this.barbers[index];
      }
      // Doc persisted but not in local cache (e.g. first write to a seeded id) — still a success.
      return { id, ...(localUpdates as any) } as Barber;
    } catch (error: any) {
      console.error('Error updating barber:', error);
      // SURFACE THE FAILURE — don't fake a save. The write did NOT reach Firestore.
      const reason = error?.code || error?.message || String(error);
      toast.error(`⚠️ Not saved to server (${reason}). Change shows locally only and will revert on reload.`, { duration: 9000 });
      // Local fallback kept for offline resilience, but the user is now warned.
      const index = this.barbers.findIndex(b => b.id === id);
      if (index !== -1) {
        this.barbers[index] = { ...this.barbers[index], ...localUpdates };
        this.saveToLocalStorage();
      }
      // Return undefined = HARD FAILURE so callers don't claim a false "saved". (Local copy
      // still updated above for offline resilience; the user has been warned.)
      return undefined;
    }
  }

  // Archive a barber (soft, REVERSIBLE): status → 'inactive'. Hidden from active lists
  // (getActiveBarbers excludes non-active) but the record + history are preserved.
  async archiveBarber(id: string): Promise<Barber | undefined> {
    return this.updateBarber(id, { status: 'inactive' });
  }

  // Restore an archived barber back to the active team.
  async restoreBarber(id: string): Promise<Barber | undefined> {
    return this.updateBarber(id, { status: 'active' });
  }

  // Permanently delete a barber from Firestore (IRREVERSIBLE). Prefer archiveBarber() for
  // anyone with appointment/transaction history — this removes the record entirely.
  async deleteBarber(id: string): Promise<void> {
    try {
      await deleteDoc(doc(this.barbersCollection, id));
    } catch (error) {
      console.error('Error deleting barber:', error);
      throw error;
    }
    this.barbers = this.barbers.filter(b => b.id !== id);
    this.saveToLocalStorage();
    this.notifyListeners();
  }

  // Save to localStorage
  private saveToLocalStorage(): void {
    localStorage.setItem('slikpos_barbers', JSON.stringify(this.barbers));
  }

  // Load from localStorage
  loadFromLocalStorage(): void {
    const stored = localStorage.getItem('slikpos_barbers');
    if (stored) {
      try {
        const parsed = JSON.parse(stored);
        // Convert date strings back to Date objects
        this.barbers = parsed.map((b: any) => ({
          ...b,
          joinDate: new Date(b.joinDate)
        }));
      } catch (error) {
        console.error('Error loading from localStorage:', error);
      }
    }
  }

  // Get barber statistics
  getBarberStats() {
    const barbers = this.getAllBarbers();
    const total = barbers.length;
    if (total === 0) {
      return {
        total: 0,
        active: 0,
        avgRating: 0,
        avgCommission: 0,
        totalMonthlyRevenue: 0,
        avgServiceTime: 0,
        totalClients: 0
      };
    }

    return {
      total,
      active: barbers.filter(b => b.status === 'active').length,
      avgRating: Number((barbers.reduce((sum, b) => sum + b.avgRating, 0) / total).toFixed(1)),
      avgCommission: Math.round(barbers.reduce((sum, b) => sum + b.commissionRate, 0) / total),
      totalMonthlyRevenue: barbers.reduce((sum, b) => sum + b.monthlyRevenue, 0),
      avgServiceTime: Math.round(barbers.reduce((sum, b) => sum + b.avgServiceTime, 0) / total),
      totalClients: barbers.reduce((sum, b) => sum + b.totalClients, 0)
    };
  }

  // Upload photo to Firebase Storage
  async uploadPhoto(barberId: string, file: File): Promise<string> {
    try {
      const storageRef = ref(storage, `barbers/${barberId}/${file.name}`);
      await uploadBytes(storageRef, file);
      const downloadURL = await getDownloadURL(storageRef);
      return downloadURL;
    } catch (error) {
      console.error('Error uploading photo:', error);
      throw error;
    }
  }

  // Delete photo from Firebase Storage
  async deletePhoto(photoURL: string): Promise<void> {
    try {
      const photoRef = ref(storage, photoURL);
      await deleteObject(photoRef);
    } catch (error) {
      console.error('Error deleting photo:', error);
      // Don't throw - photo might already be deleted
    }
  }

}

// Export singleton instance
export const barberDataService = new BarberDataService();

// Initialize from localStorage on import
barberDataService.loadFromLocalStorage();
